import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import dpiasApi from 'config/api/dpias/dpias';
import type { DpiaDetailResponse } from 'config/api/dpias/dpias.types';
import MUTATION_KEYS from 'config/api/MUTATION_KEYS';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import useUnsavedChangesProtection from 'hooks/useUnsavedChangesProtection/useUnsavedChangesProtection';
import { invalidateOwnerAssetRopaQueries } from 'pages/ProcessingActivities/_shared/invalidateOwnerAssetRopaQueries';
import {
  catchHttpStatuses,
  getUnavailableStatus,
  HTTP_UNAVAILABLE_STATUSES,
  isUnavailableQueryResult,
  unwrapAvailableQueryData,
} from 'pages/ProcessingActivities/_shared/queryUnavailable';
import useDpiaEditorContext from 'pages/ProcessingActivities/_shared/useDpiaEditorContext';

import { DpiaConflictUnresolvedError, mapDetailToFormValues, type OwnerDpiaFormValues } from './ownerDpiaEditor.helpers';
import { persistDpiaDraft, DpiaDraftSaveError, reconcileDraftIds } from './persistDpiaDraft';

type Params = {
  dpiaId: number;
  onConflict?: () => void;
  onNavigate: () => void;
  onSaveSuccess?: (closeAfterSave: boolean) => void;
  onSubmitSuccess?: () => void;
  organizationId?: number;
};

const useOwnerDpiaEditor = ({ dpiaId, onConflict, onNavigate, onSaveSuccess, onSubmitSuccess, organizationId }: Params) => {
  const queryClient = useQueryClient();
  const [formValues, setFormValues] = useState<OwnerDpiaFormValues | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Partial<Record<string, string>>>({});
  const [hasConflict, setHasConflict] = useState(false);

  const detailQuery = useQuery(
    [QUERY_KEYS.DPIA_DETAIL, organizationId, dpiaId],
    async () =>
      catchHttpStatuses(async () => {
        const { data } = await dpiasApi.getDetail(organizationId as number, dpiaId);
        return data;
      }, HTTP_UNAVAILABLE_STATUSES),
    {
      enabled: Boolean(organizationId && dpiaId),
      retry: false,
    },
  );

  const hydratedKeyRef = useRef<string>();
  const envelope = unwrapAvailableQueryData(detailQuery.data);
  const isUnavailable = isUnavailableQueryResult(detailQuery.data);
  const unavailableStatus = getUnavailableStatus(detailQuery.data);
  const { bannerMeta, context, isContextLoading, sourceTrigger } = useDpiaEditorContext({ envelope, organizationId });

  useEffect(() => {
    const key = `${String(organizationId)}:${String(dpiaId)}`;
    if (!envelope?.dpia || (isDirty && hydratedKeyRef.current === key)) {
      return;
    }

    hydratedKeyRef.current = key;
    setFormValues(mapDetailToFormValues(envelope.dpia));
    setIsDirty(false);
    setValidationErrors({});
  }, [envelope?.dpia, isDirty, organizationId, dpiaId]);

  const handleDraftFailure = useCallback(
    async (error: unknown) => {
      if (!(error instanceof DpiaDraftSaveError)) return;
      setFormValues(current => (current ? reconcileDraftIds(current, error) : error.values));
      setIsDirty(true);
      setValidationErrors(error.fields);
      try {
        const { data } = await dpiasApi.getDetail(organizationId as number, dpiaId);
        queryClient.setQueryData([QUERY_KEYS.DPIA_DETAIL, organizationId, dpiaId], data);
      } catch {
        // Preserve local edits and acknowledged IDs even when reconciliation cannot refetch.
      }
    },
    [dpiaId, organizationId, queryClient],
  );

  const handleConflictError = useCallback(
    async (error: unknown) => {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        setHasConflict(true);
        await queryClient.invalidateQueries([QUERY_KEYS.DPIA_DETAIL, organizationId, dpiaId]);
        onConflict?.();
      }
    },
    [dpiaId, onConflict, organizationId, queryClient],
  );

  const discardLocalChangesAndReload = useCallback(() => {
    setHasConflict(false);
    setIsDirty(false);
    setValidationErrors({});
  }, []);

  const allowNextNavigationRef = useRef(() => {});

  const saveMutation = useMutation(
    MUTATION_KEYS.DPIA_UPDATE,
    async (closeAfterSave: boolean) => {
      if (hasConflict) {
        throw new DpiaConflictUnresolvedError();
      }
      if (!formValues || !envelope?.dpia || !organizationId) return null;

      const data = await persistDpiaDraft(organizationId, dpiaId, envelope.dpia, formValues);
      return { closeAfterSave, data };
    },
    {
      onSuccess: result => {
        if (!result) return;

        queryClient.setQueryData([QUERY_KEYS.DPIA_DETAIL, organizationId, dpiaId], result.data);
        setFormValues(mapDetailToFormValues(result.data.dpia));
        setIsDirty(false);
        setHasConflict(false);
        if (result.closeAfterSave) {
          allowNextNavigationRef.current();
        }
        onSaveSuccess?.(result.closeAfterSave);
      },
      onError: async (error: unknown) => {
        await handleDraftFailure(error);
        await handleConflictError(error instanceof DpiaDraftSaveError ? error.originalError : error);
      },
    },
  );

  const submitMutation = useMutation(
    MUTATION_KEYS.DPIA_SUBMIT,
    async () => {
      if (hasConflict) {
        throw new DpiaConflictUnresolvedError();
      }
      if (!envelope?.dpia || !formValues || !organizationId) return null;

      let currentDetail = envelope.dpia;

      if (isDirty) {
        const data = await persistDpiaDraft(organizationId, dpiaId, currentDetail, formValues);
        queryClient.setQueryData([QUERY_KEYS.DPIA_DETAIL, organizationId, dpiaId], data);
        setFormValues(mapDetailToFormValues(data.dpia));
        setIsDirty(false);
        currentDetail = data.dpia;
      }

      const { data } = await dpiasApi.submit(organizationId, dpiaId, { revision: currentDetail.revision });
      return data;
    },
    {
      onSuccess: data => {
        if (!data) return;

        queryClient.setQueryData([QUERY_KEYS.DPIA_DETAIL, organizationId, dpiaId], data);
        setFormValues(mapDetailToFormValues(data.dpia));
        setIsDirty(false);
        setHasConflict(false);
        allowNextNavigationRef.current();
        invalidateOwnerAssetRopaQueries(queryClient, organizationId);
        onSubmitSuccess?.();
      },
      onError: async (error: unknown) => {
        await handleDraftFailure(error);
        const cause = error instanceof DpiaDraftSaveError ? error.originalError : error;
        await handleConflictError(cause);
        if (!axios.isAxiosError(cause) || cause.response?.status !== 409) {
          const cached = queryClient.getQueryData<DpiaDetailResponse>([QUERY_KEYS.DPIA_DETAIL, organizationId, dpiaId]);
          if (cached?.dpia) {
            setFormValues(mapDetailToFormValues(cached.dpia));
            setIsDirty(false);
          }
        }
      },
    },
  );

  const patchFormValues = useCallback((patch: Partial<OwnerDpiaFormValues>) => {
    setFormValues(current => (current ? { ...current, ...patch } : current));
    setIsDirty(true);
    setValidationErrors({});
  }, []);

  const handleSaveClick = useCallback(
    async (closeAfterSave: boolean) => {
      await saveMutation.mutateAsync(closeAfterSave);
    },
    [saveMutation],
  );

  const handleSubmit = useCallback(async () => {
    await submitMutation.mutateAsync();
  }, [submitMutation]);

  const { NavigationPrompt, allowNextNavigation, handleNavigation } = useUnsavedChangesProtection({
    enabled: Boolean(envelope?.dpia.canEdit),
    isDirty,
    onNavigate,
    onSave: async () => {
      await saveMutation.mutateAsync(false);
    },
  });

  allowNextNavigationRef.current = allowNextNavigation;

  return useMemo(
    () => ({
      bannerMeta,
      context,
      detail: envelope?.dpia,
      discardLocalChangesAndReload,
      formValues,
      handleNavigation,
      hasConflict,
      handleSave: handleSaveClick,
      handleSubmit,
      isDirty,
      isError: detailQuery.isError,
      isContextLoading,
      isLoading: detailQuery.isLoading,
      isUnavailable,
      unavailableStatus,
      isSaving: saveMutation.isLoading,
      isSubmitting: submitMutation.isLoading,
      NavigationPrompt,
      patchFormValues,
      refetch: detailQuery.refetch,
      saveMutation,
      setFormValues,
      setIsDirty,
      setValidationErrors,
      sourceTrigger,
      submitMutation,
      validationErrors,
    }),
    [
      bannerMeta,
      context,
      discardLocalChangesAndReload,
      detailQuery.isError,
      envelope,
      hasConflict,
      detailQuery.isLoading,
      detailQuery.refetch,
      formValues,
      handleNavigation,
      handleSaveClick,
      handleSubmit,
      isContextLoading,
      isDirty,
      isUnavailable,
      NavigationPrompt,
      patchFormValues,
      saveMutation,
      submitMutation,
      sourceTrigger,
      unavailableStatus,
      validationErrors,
    ],
  );
};

export default useOwnerDpiaEditor;
